import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import type { LedgerEntry } from '../../data/types';
import { date, money, units as formatUnits } from '../../lib/format';
import { LEDGER_LABELS } from '../../lib/labels';
import { useInvestorScope } from '../../store/selectors';
import { Card, shortEntityName } from '../common';

const OUT = new Set(['DISTRIBUTION', 'REDEMPTION', 'CAPITAL_RETURN']);

export default function TransactionList({
  entries,
  showEntity,
}: {
  entries: LedgerEntry[];
  showEntity?: boolean;
}) {
  const scope = useInvestorScope();
  const sorted = [...entries].sort(
    (left, right) => right.effectiveDate.localeCompare(left.effectiveDate) || right.seq - left.seq,
  );
  if (sorted.length === 0) {
    return (
      <Card>
        <Typography variant="body2" color="text.secondary">
          No transactions yet.
        </Typography>
      </Card>
    );
  }
  return (
    <Card sx={{ p: 0 }}>
      {sorted.map((entry, index) => {
        const holder = scope.holders.find((item) => item.id === entry.holderId);
        const fund = scope.funds.find((item) => item.id === entry.fundId);
        return (
          <Stack
            key={entry.id}
            direction="row"
            spacing={2}
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderTop: index ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {LEDGER_LABELS[entry.type]}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {date(entry.effectiveDate)} · {fund?.code}
                {showEntity && holder ? ` · ${shortEntityName(holder)}` : ''}
                {entry.memo ? ` · ${entry.memo}` : ''}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: OUT.has(entry.type)
                    ? 'success.main'
                    : entry.type === 'WITHHOLDING'
                      ? 'warning.main'
                      : 'text.primary',
                }}
              >
                {OUT.has(entry.type) ? '+' : entry.type === 'WITHHOLDING' ? '−' : ''}
                {money(entry.amount, true)}
              </Typography>
              {entry.units !== 0 && (
                <Typography variant="caption" color="text.secondary">
                  {entry.units > 0 ? '+' : ''}
                  {formatUnits(entry.units)} units
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Card>
  );
}
